import { useEffect, useCallback } from 'react';
import { useStudioStore } from '../stores/studioStore';
import { useCanvasStore } from '../stores/canvasStore';
import { useClipboardStore } from '../stores/clipboardStore';
import { usePreviewStore } from '../stores/previewStore';
import { useViewportStore } from '../stores/viewportStore';
import type { CanvasElement } from '../types';
import { Textbox } from 'fabric';
import {
  alignLeft,
  alignCenterH,
  alignRight,
  alignTop,
  alignMiddleV,
  alignBottom,
  alignAllToArtboard,
} from '../lib/alignmentUtils';

type AlignBox = { id: string; x: number; y: number; width: number; height: number };

const ALIGN_SHORTCUTS: Record<
  string,
  {
    fn: (els: AlignBox[]) => { id: string; x?: number; y?: number }[];
    dir: 'left' | 'centerH' | 'right' | 'top' | 'centerV' | 'bottom';
  }
> = {
  l: { fn: alignLeft, dir: 'left' },
  c: { fn: alignCenterH, dir: 'centerH' },
  r: { fn: alignRight, dir: 'right' },
  t: { fn: alignTop, dir: 'top' },
  m: { fn: alignMiddleV, dir: 'centerV' },
  b: { fn: alignBottom, dir: 'bottom' },
};

interface ShortcutConfig {
  toggleShortcutsPanel: () => void;
  togglePreview: () => void;
  toggleExportDialog: () => void;
}

export function useKeyboardShortcuts({ toggleShortcutsPanel, togglePreview, toggleExportDialog }: ShortcutConfig) {
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT' ||
        target.isContentEditable
      ) {
        return;
      }

      const fabricCanvas = useCanvasStore.getState().fabricCanvas;
      const activeObj = fabricCanvas?.getActiveObject();
      if (activeObj instanceof Textbox && activeObj.isEditing) return;

      const isMod = e.metaKey || e.ctrlKey;

      if ((e.key === 'p' || e.key === 'P') && !isMod) {
        if (e.shiftKey) {
          togglePreview();
        } else {
          const { isPreviewActive } = usePreviewStore.getState();
          if (!isPreviewActive) {
            e.preventDefault();
            useCanvasStore.getState().setActiveTool('pen');
          }
        }
        return;
      }

      if ((e.key === 'e' || e.key === 'E') && !isMod && !e.altKey) {
        e.preventDefault();
        toggleExportDialog();
        return;
      }

      const { isPreviewActive } = usePreviewStore.getState();
      if (isPreviewActive) return;

      const isShift = e.shiftKey;

      if (isMod && !isShift && e.key === 'z') {
        e.preventDefault();
        useStudioStore.getState().undo();
        return;
      }

      if ((isMod && isShift && e.key === 'z') || (isMod && !isShift && e.key === 'y')) {
        e.preventDefault();
        useStudioStore.getState().redo();
        return;
      }

      if (isMod && !isShift && e.key === 'c') {
        e.preventDefault();
        const studio = useStudioStore.getState();
        const selected = studio.getSelectedElements();
        if (selected.length > 0) {
          useClipboardStore.getState().copy(selected, studio.canvasState.elements);
        }
        return;
      }

      if (isMod && !isShift && e.key === 'v') {
        e.preventDefault();
        const newElements = useClipboardStore.getState().paste();
        if (newElements) {
          const newIds: string[] = [];
          for (const el of newElements) {
            useStudioStore.getState().addElement(el as CanvasElement);
            newIds.push((el as CanvasElement).id);
          }
          useStudioStore.getState().setSelectedElements(newIds);
        }
        return;
      }

      if (isMod && !isShift && e.key === 'x') {
        e.preventDefault();
        const studio = useStudioStore.getState();
        const selected = studio.getSelectedElements();
        if (selected.length > 0) {
          useClipboardStore.getState().copy(selected, studio.canvasState.elements);
          for (const el of selected) {
            useStudioStore.getState().removeElement(el.id);
          }
          useStudioStore.getState().clearSelection();
        }
        return;
      }

      if (isMod && !isShift && e.key === 'd') {
        e.preventDefault();
        const studio = useStudioStore.getState();
        const selected = studio.getSelectedElements();
        if (selected.length > 0) {
          useClipboardStore.getState().copy(selected, studio.canvasState.elements);
          const newElements = useClipboardStore.getState().paste();
          if (newElements) {
            const newIds: string[] = [];
            for (const el of newElements) {
              useStudioStore.getState().addElement(el as CanvasElement);
              newIds.push((el as CanvasElement).id);
            }
            useStudioStore.getState().setSelectedElements(newIds);
          }
        }
        return;
      }

      if (isMod && !isShift && e.key === 'g') {
        e.preventDefault();
        console.log('[GROUP-ENTRY-KEYBOARD]', {
          stage: 'cmd-g-handler-fired',
          timestamp: Date.now(),
          event: { key: e.key, metaKey: e.metaKey, ctrlKey: e.ctrlKey, shiftKey: e.shiftKey },
        });
        useStudioStore.getState().groupSelected();
        return;
      }

      if (isMod && isShift && (e.key === 'g' || e.key === 'G')) {
        e.preventDefault();
        console.log('[UNGROUP-ENTRY-KEYBOARD]', {
          stage: 'cmd-shift-g-handler-fired',
          timestamp: Date.now(),
          event: { key: e.key, metaKey: e.metaKey, ctrlKey: e.ctrlKey, shiftKey: e.shiftKey },
        });
        useStudioStore.getState().ungroupSelected();
        return;
      }

      if (isMod && isShift && (e.key === 's' || e.key === 'S')) {
        e.preventDefault();
        window.dispatchEvent(new CustomEvent('cs:open-save-template-modal'));
        return;
      }

      if (isMod && isShift && (e.key === 'i' || e.key === 'I')) {
        e.preventDefault();
        document.dispatchEvent(new CustomEvent('cs:import-svg'));
        return;
      }

      if (isMod && isShift && ALIGN_SHORTCUTS[e.key.toLowerCase()]) {
        e.preventDefault();
        const { fn, dir } = ALIGN_SHORTCUTS[e.key.toLowerCase()];
        const state = useStudioStore.getState();
        const boxes: AlignBox[] = state
          .getSelectedElements()
          .filter((el) => !el.locked)
          .map((el) => ({ id: el.id, x: el.x, y: el.y, width: el.width, height: el.height }));

        const { alignTo, canvasWidth, canvasHeight } = useCanvasStore.getState();

        let updates: { id: string; x?: number; y?: number }[];
        if (alignTo === 'artboard') {
          if (boxes.length < 1) return;
          updates = alignAllToArtboard(boxes, dir, canvasWidth, canvasHeight);
        } else {
          if (boxes.length < 2) return;
          updates = fn(boxes);
        }

        const batch = updates
          .filter((u) => u.x !== undefined || u.y !== undefined)
          .map((u) => ({
            id: u.id,
            changes: {
              ...(u.x !== undefined ? { x: u.x } : {}),
              ...(u.y !== undefined ? { y: u.y } : {}),
            } as Partial<CanvasElement>,
          }));
        if (batch.length > 0) state.updateElements(batch);
        return;
      }


      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        const selectedIds = [...useStudioStore.getState().selectedElementIds];
        for (const id of selectedIds) {
          useStudioStore.getState().removeElement(id);
        }
        useStudioStore.getState().clearSelection();
        return;
      }

      if (isMod && e.key === 'a') {
        e.preventDefault();
        const elements = useStudioStore.getState().canvasState.elements;
        useStudioStore.getState().setSelectedElements(elements.map((el) => el.id));
        return;
      }

      if (e.key === '[' && !isMod) {
        e.preventDefault();
        const selectedIds = useStudioStore.getState().selectedElementIds;
        if (selectedIds.length > 1) {
          useStudioStore.getState().reorderElements(selectedIds, 'backward');
        } else if (selectedIds.length === 1) {
          useStudioStore.getState().reorderElement(selectedIds[0], 'down');
        }
        return;
      }

      if (e.key === ']' && !isMod) {
        e.preventDefault();
        const selectedIds = useStudioStore.getState().selectedElementIds;
        if (selectedIds.length > 1) {
          useStudioStore.getState().reorderElements(selectedIds, 'forward');
        } else if (selectedIds.length === 1) {
          useStudioStore.getState().reorderElement(selectedIds[0], 'up');
        }
        return;
      }

      if (e.key === '[' && isMod) {
        e.preventDefault();
        const selectedIds = useStudioStore.getState().selectedElementIds;
        if (selectedIds.length > 1) {
          useStudioStore.getState().reorderElements(selectedIds, 'back');
        } else if (selectedIds.length === 1) {
          useStudioStore.getState().reorderElement(selectedIds[0], 'bottom');
        }
        return;
      }

      if (e.key === ']' && isMod) {
        e.preventDefault();
        const selectedIds = useStudioStore.getState().selectedElementIds;
        if (selectedIds.length > 1) {
          useStudioStore.getState().reorderElements(selectedIds, 'front');
        } else if (selectedIds.length === 1) {
          useStudioStore.getState().reorderElement(selectedIds[0], 'top');
        }
        return;
      }

      if (
        e.key === 'ArrowUp' ||
        e.key === 'ArrowDown' ||
        e.key === 'ArrowLeft' ||
        e.key === 'ArrowRight'
      ) {
        const studio = useStudioStore.getState();
        const selected = studio.getSelectedElements();
        if (selected.length === 0) return;
        e.preventDefault();
        const step = isShift ? 10 : 1;
        const dx = e.key === 'ArrowLeft' ? -step : e.key === 'ArrowRight' ? step : 0;
        const dy = e.key === 'ArrowUp' ? -step : e.key === 'ArrowDown' ? step : 0;
        for (const el of selected) {
          if (el.locked) continue;
          studio.updateElement(el.id, {
            x: (el.x ?? 0) + dx,
            y: (el.y ?? 0) + dy,
          } as Partial<CanvasElement>);
        }
        return;
      }

      if (e.key === 'Escape') {
        e.preventDefault();
        const currentTool = useCanvasStore.getState().activeTool;
        if (currentTool !== 'select' && currentTool !== 'pan') {
          useCanvasStore.getState().setActiveTool('select');
        } else {
          useStudioStore.getState().clearSelection();
          fabricCanvas?.discardActiveObject();
          fabricCanvas?.requestRenderAll();
        }
        return;
      }

      if (e.key === 'v' && !isMod) {
        useCanvasStore.getState().setActiveTool('select');
        return;
      }

      if (e.key === 't' && !isMod) {
        useCanvasStore.getState().setActiveTool('text');
        return;
      }

      if (e.key === 'r' && !isMod) {
        useCanvasStore.getState().setPendingShapeType('rectangle');
        useCanvasStore.getState().setActiveTool('shape');
        return;
      }

      if (e.key === 'o' && !isMod) {
        useCanvasStore.getState().setPendingShapeType('circle');
        useCanvasStore.getState().setActiveTool('shape');
        return;
      }

      if (e.key === 'l' && !isMod) {
        useCanvasStore.getState().setActiveTool('line');
        return;
      }

      if (e.key === 'b' && !isMod) {
        useCanvasStore.getState().setActiveTool('button');
        return;
      }

      if (e.key === 'i' && !isMod) {
        useCanvasStore.getState().setActiveTool('image');
        return;
      }

      if (e.key === 'h' && !isMod && !isShift) {
        useCanvasStore.getState().setActiveTool('pan');
        return;
      }

      if ((e.key === 'H' || (e.key === 'h' && isShift)) && !isMod) {
        e.preventDefault();
        const cw = useCanvasStore.getState().canvasWidth;
        const ch = useCanvasStore.getState().canvasHeight;
        const fc = useCanvasStore.getState().fabricCanvas;
        const el = (fc as any)?.lowerCanvasEl as HTMLCanvasElement | undefined;
        if (el) {
          useViewportStore.getState().homeToggle(cw, ch, el.clientWidth, el.clientHeight);
        }
        return;
      }

      if (e.key === '0' && !isMod && !isShift) {
        e.preventDefault();
        const cw = useCanvasStore.getState().canvasWidth;
        const ch = useCanvasStore.getState().canvasHeight;
        const fc = useCanvasStore.getState().fabricCanvas;
        const el = (fc as any)?.lowerCanvasEl as HTMLCanvasElement | undefined;
        if (el) {
          useViewportStore.getState().centerAtZoom(cw, ch, el.clientWidth, el.clientHeight, 1.0);
        } else {
          useViewportStore.getState().zoomReset();
        }
        return;
      }

      if (isMod && (e.key === '=' || e.key === '+')) {
        e.preventDefault();
        useViewportStore.getState().zoomIn();
        return;
      }

      if (isMod && e.key === '-') {
        e.preventDefault();
        useViewportStore.getState().zoomOut();
        return;
      }

      if (isMod && e.key === '0') {
        e.preventDefault();
        const cw = useCanvasStore.getState().canvasWidth;
        const ch = useCanvasStore.getState().canvasHeight;
        const fc = useCanvasStore.getState().fabricCanvas;
        const el = (fc as any)?.lowerCanvasEl as HTMLCanvasElement | undefined;
        if (el) {
          useViewportStore.getState().centerAtZoom(cw, ch, el.clientWidth, el.clientHeight, 1.0);
        } else {
          useViewportStore.getState().zoomReset();
        }
        return;
      }

      if (isMod && e.key === '1') {
        e.preventDefault();
        const cw = useCanvasStore.getState().canvasWidth;
        const ch = useCanvasStore.getState().canvasHeight;
        const fabricCanvas = useCanvasStore.getState().fabricCanvas;
        const el = (fabricCanvas as any)?.lowerCanvasEl as HTMLCanvasElement | undefined;
        if (el) {
          useViewportStore.getState().fitToScreen(cw, ch, el.clientWidth, el.clientHeight);
        }
        return;
      }

      if (e.key === 'g' && !isMod) {
        useViewportStore.getState().toggleGrid();
        return;
      }

      if (e.key === 'r' && isMod) {
        e.preventDefault();
        useCanvasStore.getState().toggleRulers();
        return;
      }

      if (e.key === ';' && isMod && isShift) {
        e.preventDefault();
        useViewportStore.getState().clearAllGuides();
        return;
      }

      if (e.key === ';' && isMod && !isShift) {
        e.preventDefault();
        useViewportStore.getState().toggleGuides();
        return;
      }

      if (isMod && isShift && (e.key === 'u' || e.key === 'U')) {
        e.preventDefault();
        const studio = useStudioStore.getState();
        const selected = studio.getSelectedElements();
        const textEl = selected.find((el) => el.type === 'text');
        if (!textEl) return;

        if ((textEl as any).textPath) {
          studio.updateElement(textEl.id, { textPath: undefined } as any);
          return;
        }

        const paths = studio.canvasState.elements.filter((el) => el.type === 'path');
        if (paths.length !== 1) return;

        studio.updateElement(textEl.id, {
          textPath: {
            pathElementId: paths[0].id,
            startOffset: 0,
            side: 'left',
            align: 'baseline',
          },
          pathData: undefined,
          pathStartOffset: undefined,
          pathSide: undefined,
          pathAlign: undefined,
        } as any);
        return;
      }

      if (e.key === '?' || (isShift && e.key === '/')) {
        e.preventDefault();
        toggleShortcutsPanel();
        return;
      }
    },
    [toggleShortcutsPanel, togglePreview, toggleExportDialog]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);
}
